import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Input, Table, Button, Modal, Form, Typography, Select, notification, Row, Col } from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined, PercentageOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ProductService from "services/product";
import { number_to_price, price_to_number, removeSpaces, removeSpecialChars, formatPriceNoDecimal } from "helper/common";
import { generalUpperCase } from "helper/stringUtils";
import { MIN_COLUMN_WIDTH, EXTRA_BIG_COLUMND_WITDTH } from "constants/app";
import ProductCategorySelect from "./_components/ProductCategorySelect";
import "./styles.scss";

const { Text } = Typography;

// Hàm tạo mã SKU tự động từ tên sản phẩm (slug 12 ký tự + hash 8 ký tự)
const generateSKU = (name) => {
  if (!name) return "";
  
  // Tạo slug từ 12 ký tự đầu
  const slug = removeSpecialChars(removeSpaces(generalUpperCase(name))).substring(0, 12);
  
  // Tạo hash 8 ký tự từ toàn bộ tên
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) + hash) + name.charCodeAt(i);
    hash = hash & hash;
  }
  const hashStr = Math.abs(hash).toString(36).toUpperCase().substring(0, 8);
  
  return `${slug}-${hashStr}`;
};

const ProductSettings = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [generatedSKU, setGeneratedSKU] = useState("");

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  const reloadProducts = useCallback(async (searchQuery = "") => {
    try {
      setLoading(true);
      const q = (searchQuery || "").trim();
      const payload = { limit: 1000 };
      if (q) {
        payload.searchText = q;
      }
      const res = await ProductService.advanceUserGetList(payload);
      const list = Array.isArray(res) ? res : res?.data || [];
      const mapped = (list || []).map((p) => ({
        id: p.productId ?? p.id,
        name: p.productName ?? p.name,
        price: p.price ?? p.productPrice ?? p.unitPrice ?? 0,
        category: p.productCategory,
        usageDate: p.usageDate ?? 0,
        productTax: p.productTax,
      }));
      setProducts(mapped);
    } catch (e) {
      console.error('Failed to reload products:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await ProductService.categoryAdvanceUserGetList({ limit: 100, includeSystem: false });
      const list = Array.isArray(response) ? response : response?.data || [];
      setCategories(list);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    if (!hasInitialLoad) {
      reloadProducts("");
      loadCategories();
      setHasInitialLoad(true);
    }
  }, []);

  const openAdd = () => {
    setEditingProduct(null);
    setGeneratedSKU("");
    form.resetFields();
    form.setFieldsValue({ productTax: 10 });
    setIsModalVisible(true);
  };

  const openEdit = (record) => {
    setEditingProduct(record);
    const sku = generateSKU(record.name);
    setGeneratedSKU(sku);
    
    // Chuyển đổi productTax từ số thập phân (0.1) sang phần trăm (10)
    const productTaxPercent = record.productTax !== null && record.productTax !== undefined 
      ? Math.round(record.productTax * 100)
      : 10;
    
    form.setFieldsValue({
      name: record.name,
      price: number_to_price(record.price),
      productTax: productTaxPercent,
      productCategory: record.category,
    });
    setIsModalVisible(true);
  };

  const handleProductNameChange = (e) => {
    const name = e.target.value;
    form.setFieldsValue({ name });
    const sku = generateSKU(name);
    setGeneratedSKU(sku);
  };

  const handleNumericInput = (e) => {
    const char = e.key;
    if (!/\d/.test(char) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(char)) {
      e.preventDefault();
    }
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;
    const numericValue = String(value).replace(/\D/g, '');
    const formatted = number_to_price(numericValue);
    form.setFieldsValue({ price: formatted });
  };

  const handleSearch = () => {
    reloadProducts(query);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.productCategoryId === categoryId || cat.id === categoryId);
    return category ? (category.productCategoryName || category.name) : t("product.uncategorized");
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const priceNum = price_to_number(String(values.price).replace(/\./g, ''));
      
      // Chuyển % sang số thập phân (10% = 0.1)
      const productTax = (values.productTax || 0) / 100;
      
      setLoading(true);
      
      if (editingProduct) {
        const payload = {
          id: editingProduct.id,
          data: {
            productSKU: generatedSKU,
            productName: values.name,
            productPrice: priceNum,
            productTax: productTax,
            productCategory: values.productCategory,
          },
        };
        
        const response = await ProductService.advanceUserUpdateBy(payload);
        
        if (response?.statusCode !== 200) {
          const errorMessage = response?.error === "PRODUCT_DUPLICATE_SKU" 
            ? "Sản phẩm đã tồn tại" 
            : response?.message || t("product.editFail");
          notification['error']({
            message: "",
            description: errorMessage
          });
          setLoading(false);
          return;
        }
        
        notification['success']({
          message: "",
          description: t("product.editedMessage", { name: values.name, price: formatPriceNoDecimal(priceNum) })
        });
        
        setIsModalVisible(false);
        reloadProducts(query);
        loadCategories();
      } else {
        const payload = {
          productSKU: generatedSKU,
          productName: values.name,
          productPrice: priceNum,
          productTax: productTax,
          productCategory: values.productCategory,
        };
        
        const response = await ProductService.advanceUserCreate(payload);
        if (response?.statusCode !== 200) {
          const errorMessage = response?.error === "PRODUCT_DUPLICATE_SKU" 
            ? "Sản phẩm đã tồn tại" 
            : response?.message || t("product.addFail");
          notification['error']({
            message: "",
            description: errorMessage
          });
          setLoading(false);
          return;
        }
        
        notification['success']({
          message: "",
          description: t("product.addedMessage", { name: values.name, price: formatPriceNoDecimal(priceNum) })
        });
        
        setIsModalVisible(false);
        reloadProducts(query);
        loadCategories();
      }
    } catch (err) {
      notification['error']({
        message: "",
        description: t("product.saveFail")
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (record) => {
    setDeletingProduct(record);
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!deletingProduct) return;
    
    setLoading(true);
    try {
      const response = await ProductService.advanceUserDeleteBy({ id: deletingProduct.id });
      
      if (response?.statusCode !== 200) {
        notification['error']({
          message: "",
          description: response?.message || t("product.deleteFail")
        });
        setLoading(false);
        return;
      }
      
      notification['success']({
        message: "",
        description: t("product.deletedMessage", { name: deletingProduct.name })
      });
      
      setIsDeleteModalVisible(false);
      setDeletingProduct(null);
      reloadProducts(query);
      loadCategories();
    } catch (e) {
      notification['error']({
        message: "",
        description: t("product.deleteFail")
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "STT", dataIndex: "id", key: "id", width: 25, align: 'center', render: (_, __, idx) => idx + 1 },
    { title: t("product.productName"), dataIndex: "name", key: "name", width: "40%", render: (val) => <Text className="ps-cell-multiline-clamp">{val}</Text> },
    {
      title: t("product.category"),
      dataIndex: "category",
      key: "category",
      width: "25%",
      render: (categoryId) => <Text className="ps-cell-multiline-clamp">{getCategoryName(categoryId)}</Text>
    },
    {
      title: <div style={{ textAlign: 'right' }}>{t("product.price")}</div>,
      dataIndex: "priceAndActions",
      key: "priceAndActions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <div className="ps-price-row">
          <div className="ps-price-cell">
            <Text className="ps-price-value-multiline">{formatPriceNoDecimal(record.price)}đ</Text>
          </div>
          {isEditMode && (
            <div className="ps-price-actions">
              <Button type="link" size="small" className="ps-action-btn" icon={<EditOutlined />} onClick={() => openEdit(record)} disabled={loading} />
              <Button type="link" size="small" className="ps-action-btn" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={loading} />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="ps-wrapper">
      <Row gutter={[24,24]}>
        <Col xs={24} md={8} lg={6} xl={4}>
          <Input.Search
            className="ps-search-input"
            autoFocus
            placeholder={t("product.searchProducts")}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>
      </Row>
      <div className="ps-search-container">
        {/* <Input
          placeholder={t("product.searchProducts")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={handleSearch}
          suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
          className="ps-search-input"
        /> */}
        
        <div className="ps-search-spacer" />
      </div>

      <div className="ps-content-wrapper">
        <div className="ps-list-title">{t("product.sellingProducts")}</div>

        <Table
          dataSource={products}
          columns={columns}
          rowKey={(r) => r.id}
          pagination={false}
          bordered={false}
          showHeader={true}
          loading={loading}
          className="ps-table-transparent"
          scroll={{ y: 360 }}
        />
      </div>

      <div className="ps-actions-container">
        {!isEditMode ? (
          <>
            <Button
              onClick={() => setIsEditMode(true)}
              disabled={loading}
              size="middle"
              className="ps-button-edit"
            >
              {t("product.edit")}
            </Button>
            <Button
              type="primary"
              onClick={openAdd}
              disabled={loading}
              size="middle"
              className="ps-button-add"
            >
              {t("product.add")}
            </Button>
          </>
        ) : (
          <Button type="primary" onClick={() => setIsEditMode(false)} disabled={loading} size="middle" className="ps-button-edit">{t("product.complete")}</Button>
        )}
      </div>

      {/* Modal chỉnh sửa/thêm sản phẩm */}
      <Modal
        title={
            <div className="ps-modal-title">{editingProduct ? t("product.editTitle") : t("product.addTitle")}</div>
        }
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
        className="edit-modal"
      >
        <Form form={form} layout="vertical" className="ps-modal-form">
          <Form.Item 
            name="name" 
            label={t("product.productName")} 
            rules={[
              { required: true, message: t("product.enterProductName") },
              { 
                validator: (_, value) => 
                  value?.trim() ? Promise.resolve() : Promise.reject(new Error(t("product.enterProductName")))
              }
            ]}
          >
            <Input 
              placeholder={t("product.enterProductName")} 
              onChange={handleProductNameChange}
            />
          </Form.Item>

          <Form.Item 
            name="productCategory" 
            label={t("product.category")}
            rules={[{ required: true, message: t("product.selectCategory") }]}
          >
            <ProductCategorySelect 
              categories={categories} 
              onReloadCategories={loadCategories} 
              placeholder={t("product.selectCategory")}
              onCheckUsage={(categoryId) => products.some(p => p.category === categoryId)}
            />
          </Form.Item>

          <Form.Item 
            name="price" 
            label={t("product.price")} 
            rules={[
              { required: true, message: t("product.enterProductPrice") },
              {
                validator: (_, value) => {
                  const numericValue = Number(String(value || '').replace(/\D/g, ''));
                  if (numericValue === 0) {
                    return Promise.resolve();
                  }
                  if (numericValue >= 1000) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("product.priceMinDigits")));
                }
              }
            ]}
          >
            <Input 
              placeholder={t("product.enterProductPrice")} 
              addonAfter="VND" 
              onChange={handlePriceChange}
              onKeyDown={handleNumericInput}
            />
          </Form.Item>

          <Form.Item 
            name="productTax" 
            label="Thuế sản phẩm"
            rules={[
              { required: true, message: "Vui lòng nhập thuế" },
              { pattern: /^\d+(\.\d{1,2})?$/, message: "Thuế phải là số" }
            ]}
          >
            <Input 
              placeholder="10" 
              addonAfter={<PercentageOutlined />}
              onKeyDown={handleNumericInput}
            />
          </Form.Item>

          <div className="ps-modal-actions">
            <Button 
              onClick={() => setIsModalVisible(false)} 
              className="ps-modal-cancel-btn"
              disabled={loading}
            >
              {t("product.close")}
            </Button>
            <Button 
              type="primary" 
              onClick={handleSave} 
              loading={loading}
              className="ps-modal-save-btn"
            >
              {t("product.save")}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal xác nhận xóa sản phẩm */}
      <Modal
        title={
            <div className="ps-modal-title">{t("product.deleteConfirmTitle")}</div>
        }
        visible={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setDeletingProduct(null);
        }}
        footer={null}
        centered
        className="confirm-modal"
      >
        <div className="ps-modal-body-text">
          <Text className="ps-body-text">
            {deletingProduct ? t("product.deleteConfirmMessage", { name: deletingProduct.name }) : ''}
          </Text>
        </div>

        <div className="ps-modal-actions">
          <Button 
            onClick={() => {
              setIsDeleteModalVisible(false);
              setDeletingProduct(null);
            }} 
            className="ps-modal-cancel-btn"
            disabled={loading}
          >
            {t("product.cancel")}
          </Button>
          <Button 
            type="primary"
            onClick={confirmDelete} 
            loading={loading}
            className="ps-modal-delete-btn"
          >
            {t("product.confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProductSettings;
